import { useState, useEffect, useCallback, useMemo } from "react";
import { AreaChart, Area, BarChart, Bar, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from 'recharts';

var API = 'https://api.algrok.com';
function fmtRs(n){if(n==null||isNaN(n))return'--';return(n>=0?'+':'')+Math.round(n).toLocaleString('en-IN')+'\u20B9';}
function pc(n){return n>=0?'#00d4aa':'#ff4466';}
function fmtPct(n){if(n==null||isNaN(n))return'--';return n.toFixed(1)+'%';}
var MONTHS=['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
var DAYS_F=['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
var axTick={fill:'#4a6080',fontSize:10};

function fmtDateLabel(iso,range){
  if(!iso)return'';
  var parts=iso.split('-');
  var d=parseInt(parts[2]),m=parseInt(parts[1])-1;
  if(range<=90)return d+' '+MONTHS[m];
  return MONTHS[m]+' '+parts[0].slice(2);
}


function useWindowWidth() {
  var get = function() { return window.innerWidth; };
  var [w, setW] = useState(get());
  useEffect(function() { var h = function() { setW(get()); }; window.addEventListener('resize', h); return function() { window.removeEventListener('resize', h); }; }, []);
  return w;
}

export default function TradingJournal(){
  var [data,setData]=useState(null);
  var winW = useWindowWidth();
  var isCompact = winW < 1080;
  var isTight = winW < 900;
  var [loading,setLoading]=useState(true);
  var [refreshing,setRefreshing]=useState(false);
  var [tab,setTab]=useState('Dashboard');
  var [chartTab,setChartTab]=useState('equity');
  var [chartZoom,setChartZoom]=useState('all');
  var [calView,setCalView]=useState('year');
  var [calMonth,setCalMonth]=useState(new Date().getMonth());
  var [calYear,setCalYear]=useState(new Date().getFullYear());
  var [selectedAlgo,setSelectedAlgo]=useState(null); // null=all, string=strategy key (e.g. 'tv','jp')
  var [perLot,setPerLot]=useState(false); // divide monetary values by each day's traded lots
  var TABS=['Dashboard','Strategies','Calendar'];

  var fetchData=useCallback(function(){
    fetch(API+'/journal-data').then(function(r){return r.json();}).then(function(d){setData(d);setLoading(false);}).catch(function(){setLoading(false);});
  },[]);
  useEffect(function(){fetchData();},[fetchData]);

  var doRefresh=function(){
    setRefreshing(true);
    fetch(API+'/journal-update',{method:'POST'}).then(function(){return fetch(API+'/journal-data');}).then(function(r){return r.json();}).then(function(d){setData(d);setRefreshing(false);}).catch(function(){setRefreshing(false);});
  };

  var [backfilling,setBackfilling]=useState(false);
  var doBackfill=function(){
    setBackfilling(true);
    fetch(API+'/journal-backfill',{method:'POST'}).then(function(r){return r.json();}).then(function(res){
      if(res.imported>0){fetchData();}
      setBackfilling(false);
      alert('Backfill: '+(res.imported||0)+' days imported from trade history');
    }).catch(function(){setBackfilling(false);});
  };

  var rawDaily=useMemo(function(){return data?data.daily||{}:{};},[data]);
  var rawStrategies=useMemo(function(){return data?data.strategies||{}:{};},[data]);

  // Per-lot view: divide each strategy-day's monetary fields by THAT day's lots
  // (recorded by journal_store from leg qty; days without lots data assume 1),
  // then rebuild day summaries and strategy cumulatives from the adjusted days.
  // Counts (trades/wins/losses) are lot-independent and pass through untouched.
  var daily=useMemo(function(){
    if(!perLot)return rawDaily;
    var out={};
    Object.keys(rawDaily).forEach(function(d){
      var day=rawDaily[d],nd={},sumT=0,sumR=0,sumU=0,tr=0,w=0,l=0;
      Object.keys(day).forEach(function(k){
        if(k==='_summary')return;
        var sd=day[k]||{};var lots=sd.lots;
        var ns;
        if(lots){
          ns=Object.assign({},sd,{
            realized:Math.round(((sd.realized||0)/lots)*100)/100,
            unrealized:Math.round(((sd.unrealized||0)/lots)*100)/100,
            total:Math.round(((sd.total||0)/lots)*100)/100});
        }else{
          // Day predates lots tracking — per-lot series starts fresh from the
          // first lots-recorded snapshot; untracked days contribute nothing.
          ns=Object.assign({},sd,{realized:0,unrealized:0,total:0,trades:0,wins:0,losses:0});
        }
        nd[k]=ns;sumT+=ns.total;sumR+=ns.realized;sumU+=ns.unrealized;
        tr+=(ns.trades||0);w+=(ns.wins||0);l+=(ns.losses||0);
      });
      nd._summary=Object.assign({},day._summary||{},{total:Math.round(sumT*100)/100,realized:Math.round(sumR*100)/100,unrealized:Math.round(sumU*100)/100,trades:tr,wins:w,losses:l});
      out[d]=nd;
    });
    return out;
  },[rawDaily,perLot]);
  var strategies=useMemo(function(){
    if(!perLot)return rawStrategies;
    var out={};
    Object.keys(rawStrategies).forEach(function(sid){
      var cum=0,tt=0,tw=0,tl=0;
      Object.keys(daily).forEach(function(d){var sd=daily[d][sid];if(sd){cum+=(sd.total||0);tt+=(sd.trades||0);tw+=(sd.wins||0);tl+=(sd.losses||0);}});
      out[sid]=Object.assign({},rawStrategies[sid],{cumulative_pnl:Math.round(cum*100)/100,total_trades:tt,total_wins:tw,total_losses:tl});
    });
    return out;
  },[rawStrategies,daily,perLot]);

  // Build daily chart data — filtered by selectedAlgo, skip empty days
  var dailyData=useMemo(function(){
    var dates=Object.keys(daily).sort();
    var cum=0,peak=0,result=[];
    dates.forEach(function(d){
      var pnl=0,trades=0,wins=0,losses=0;
      if(selectedAlgo){
        var sd=daily[d][selectedAlgo];
        if(sd){pnl=sd.total||0;trades=sd.trades||0;wins=sd.wins||0;losses=sd.losses||0;}
      }else{
        var s=daily[d]._summary||{};
        pnl=s.total||0;trades=s.trades||0;wins=s.wins||0;losses=s.losses||0;
      }
      // Skip days with zero PnL AND zero trades (weekends, holidays, no-activity days)
      if(pnl===0&&trades===0)return;
      cum+=pnl;if(cum>peak)peak=cum;
      result.push({date:d,pnl:Math.round(pnl),equity:Math.round(cum),dd:Math.round(-(peak-cum)),trades:trades,wins:wins,losses:losses});
    });
    return result;
  },[daily,selectedAlgo]);

  // Apply zoom
  var filteredDaily=useMemo(function(){
    if(!dailyData.length)return dailyData;
    if(chartZoom==='all')return dailyData;
    var now=new Date();var cutoff;
    if(chartZoom==='1w'){cutoff=new Date(now);cutoff.setDate(cutoff.getDate()-7);}
    else if(chartZoom==='1m'){cutoff=new Date(now);cutoff.setMonth(cutoff.getMonth()-1);}
    else if(chartZoom==='3m'){cutoff=new Date(now);cutoff.setMonth(cutoff.getMonth()-3);}
    else if(chartZoom==='6m'){cutoff=new Date(now);cutoff.setMonth(cutoff.getMonth()-6);}
    else if(chartZoom==='ytd'){cutoff=new Date(now.getFullYear(),0,1);}
    else return dailyData;
    var cutISO=cutoff.toISOString().split('T')[0];
    var filtered=dailyData.filter(function(d){return d.date>=cutISO;});
    if(!filtered.length)return filtered;
    var cum=0,peak=0;
    return filtered.map(function(d){cum+=d.pnl;if(cum>peak)peak=cum;return Object.assign({},d,{equity:Math.round(cum),dd:Math.round(-(peak-cum))});});
  },[dailyData,chartZoom]);

  var dateRange=filteredDaily.length>1?Math.round((new Date(filteredDaily[filteredDaily.length-1].date)-new Date(filteredDaily[0].date))/86400000):0;

  // Hide removed/deprecated strategies from journal display (both current + historical aliases)
  var HIDE_STRAT={'auto_dn':true,'autodn':true,'quantiply':true,'straddle':true,'hedged_selling':true,'delta_neutral':true,'true_dn':true,'dn':true,
    // Retired Jun 2026 — removed from engine, hide historical entries from journal
    'prem_asym':true,'prem_asym_1':true,'prem_asym_2':true,'prem_asym_3':true,'premasym':true,'pa':true,'pa1':true,'pa2':true,'pa3':true,
    'scalp_radar':true,'scalpradar':true,'scalp':true,
    'amb_hedged':true,'amb_hedged_1':true,'amb_hedged_2':true,'amb_hedged_3':true,'ambhedged':true,'ambh1':true,'ambh2':true,'ambh3':true};
  var visibleStrats=Object.entries(strategies).filter(function(p){return !HIDE_STRAT[p[0]];}).map(function(p){return p[1];});

  // Totals — filtered by selectedAlgo
  var totalP,totalT,totalW,totalL,winR,daysRecorded;
  if(selectedAlgo){
    var sa=strategies[selectedAlgo]||{};
    totalP=sa.cumulative_pnl||0;totalT=sa.total_trades||0;totalW=sa.total_wins||0;totalL=sa.total_losses||0;
    winR=(totalW+totalL)>0?(totalW/(totalW+totalL)*100):0;
    daysRecorded=dailyData.filter(function(d){return d.pnl!==0||d.trades>0;}).length;
  }else{
    totalP=visibleStrats.reduce(function(a,s){return a+(s.cumulative_pnl||0);},0);
    totalT=visibleStrats.reduce(function(a,s){return a+(s.total_trades||0);},0);
    totalW=visibleStrats.reduce(function(a,s){return a+(s.total_wins||0);},0);
    totalL=visibleStrats.reduce(function(a,s){return a+(s.total_losses||0);},0);
    winR=(totalW+totalL)>0?(totalW/(totalW+totalL)*100):0;
    daysRecorded=dailyData.length;
  }
  var selectedAlgoInfo=selectedAlgo?strategies[selectedAlgo]:null;

  // Risk metrics computed from daily P&L series
  var riskMetrics=useMemo(function(){
    var vals=dailyData.filter(function(d){return d.pnl!==0||d.trades>0;}).map(function(d){return d.pnl;});
    if(vals.length<2)return{sharpe:0,sortino:0,maxDD:0,profitFactor:0,avgDaily:0};
    var mean=vals.reduce(function(a,b){return a+b;},0)/vals.length;
    // Sharpe: (mean / std) * sqrt(252)
    var variance=vals.reduce(function(a,v){return a+Math.pow(v-mean,2);},0)/(vals.length-1);
    var std=Math.sqrt(variance);
    var sharpe=std>0?Math.round((mean/std)*Math.sqrt(252)*100)/100:0;
    // Sortino: (mean / downside_dev) * sqrt(252)
    var downside=vals.filter(function(v){return v<0;}).map(function(v){return v*v;});
    var downsideDev=downside.length>0?Math.sqrt(downside.reduce(function(a,b){return a+b;},0)/downside.length):0;
    var sortino=downsideDev>0?Math.round((mean/downsideDev)*Math.sqrt(252)*100)/100:0;
    // Max Drawdown
    var peak=0,maxDD=0;
    var cum=0;
    vals.forEach(function(v){cum+=v;if(cum>peak)peak=cum;var dd=peak-cum;if(dd>maxDD)maxDD=dd;});
    // Profit Factor
    var grossWin=vals.filter(function(v){return v>0;}).reduce(function(a,b){return a+b;},0);
    var grossLoss=Math.abs(vals.filter(function(v){return v<0;}).reduce(function(a,b){return a+b;},0));
    var pf=grossLoss>0?Math.round(grossWin/grossLoss*100)/100:grossWin>0?Infinity:0;
    return{sharpe:sharpe,sortino:sortino,maxDD:Math.round(maxDD),profitFactor:pf,avgDaily:Math.round(mean)};
  },[dailyData]);

  var card={background:'rgba(13,21,38,0.8)',borderRadius:8,padding:16,border:'1px solid rgba(26,45,74,0.4)'};
  var metric=function(l,v,c,sub){return<div key={l} style={{background:'rgba(8,14,29,0.6)',borderRadius:6,padding:'10px 12px',border:'1px solid rgba(26,45,74,0.25)'}}><div style={{fontSize:9,color:'#556677',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:4}}>{l}</div><div style={{fontSize:18,fontWeight:900,color:c||'#e2e8f0'}}>{v}</div>{sub&&<div style={{fontSize:10,color:'#556677',marginTop:3}}>{sub}</div>}</div>;};

  if(loading)return<div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100%',background:'#080e1d',color:'#556677'}}>Loading Journal...</div>;

  // Charts
  var renderCharts=function(){
    if(!filteredDaily.length)return<div style={{color:'#3d4f63',textAlign:'center',padding:40}}>No data yet. Click Update to snapshot.</div>;
    var chartTabs=[{id:'equity',label:'Cumulative Profit'},{id:'pnl',label:'Daily P&L'},{id:'drawdown',label:'Drawdown'}];
    var zoomBtns=[['1w','1W'],['1m','1M'],['3m','3M'],['6m','6M'],['ytd','YTD'],['all','All']];
    var lastEq=filteredDaily[filteredDaily.length-1];
    var tickInterval=dateRange>180?Math.floor(filteredDaily.length/8):dateRange>60?Math.floor(filteredDaily.length/10):0;
    var xFmt=function(v){return fmtDateLabel(v,dateRange);};
    var yFmt=function(v){return v>=10000||v<=-10000?(v/1000).toFixed(0)+'k':v>=1000||v<=-1000?(v/1000).toFixed(1)+'k':v;};

    var tip=function(props){
      if(!props.active||!props.payload||!props.payload.length)return null;
      var d=props.payload[0].payload;
      return<div style={{background:'rgba(30,41,59,0.95)',border:'1px solid rgba(100,130,170,0.3)',borderRadius:8,padding:'10px 14px',fontSize:11,boxShadow:'0 4px 20px rgba(0,0,0,0.4)'}}>
        <div style={{color:'#cbd5e1',marginBottom:4,fontWeight:600}}>{fmtDateLabel(d.date,0)}</div>
        {chartTab==='pnl'&&<div style={{color:pc(d.pnl),fontWeight:900,fontSize:16}}>P&L: {fmtRs(d.pnl)}</div>}
        {chartTab==='equity'&&<div style={{color:pc(d.equity),fontWeight:900,fontSize:16}}>Equity: {fmtRs(d.equity)}</div>}
        {chartTab==='drawdown'&&<div style={{color:'#ff4466',fontWeight:900,fontSize:16}}>DD: {fmtRs(d.dd)}</div>}
        <div style={{color:'#94a3b8',fontSize:10,marginTop:3}}>{d.trades} trades | {d.wins}W {d.losses}L</div>
      </div>;
    };

    var eqColor=selectedAlgoInfo?selectedAlgoInfo.color:(lastEq.equity>=0?'#00d4aa':'#ff4466');

    return<div style={Object.assign({},card,{marginBottom:16})}>
      <div style={{display:'flex',borderBottom:'1px solid rgba(26,45,74,0.3)',marginBottom:12}}>
        {chartTabs.map(function(t){return<button key={t.id} onClick={function(){setChartTab(t.id);}} style={{padding:'8px 20px',fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:'inherit',border:'none',background:chartTab===t.id?'rgba(14,165,233,0.1)':'transparent',color:chartTab===t.id?'#0ea5e9':'#556677',borderBottom:chartTab===t.id?'2px solid #0ea5e9':'2px solid transparent'}}>{t.label}</button>;})}
      </div>
      <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:10,flexWrap:'wrap'}}>
        <span style={{fontSize:9,color:'#556677',fontWeight:700}}>Zoom</span>
        {zoomBtns.map(function(z){return<button key={z[0]} onClick={function(){setChartZoom(z[0]);}} style={{padding:'2px 8px',borderRadius:3,fontSize:9,fontWeight:700,cursor:'pointer',fontFamily:'inherit',background:chartZoom===z[0]?'rgba(14,165,233,0.15)':'transparent',color:chartZoom===z[0]?'#0ea5e9':'#556677',border:'1px solid '+(chartZoom===z[0]?'rgba(14,165,233,0.3)':'rgba(26,45,74,0.2)')}}>{z[1]}</button>;})}
      </div>
      {chartTab==='equity'&&<ResponsiveContainer width="100%" height={250}>
        <AreaChart data={filteredDaily} margin={{top:5,right:10,left:0,bottom:5}}>
          <defs><linearGradient id="eqG" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={eqColor} stopOpacity={0.2}/><stop offset="95%" stopColor={eqColor} stopOpacity={0}/></linearGradient></defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(26,45,74,0.15)" vertical={false}/>
          <XAxis dataKey="date" tick={axTick} axisLine={false} tickLine={false} tickFormatter={xFmt} interval={tickInterval||'preserveStartEnd'}/>
          <YAxis tick={axTick} axisLine={false} tickLine={false} tickFormatter={yFmt} width={50}/>
          <ReferenceLine y={0} stroke="rgba(26,45,74,0.4)" strokeDasharray="3"/>
          <Tooltip content={tip}/><Area type="monotone" dataKey="equity" stroke={eqColor} strokeWidth={2} fill="url(#eqG)" dot={false} activeDot={{r:4,stroke:'#0f1929',strokeWidth:2}}/>
        </AreaChart>
      </ResponsiveContainer>}
      {chartTab==='pnl'&&<ResponsiveContainer width="100%" height={250}>
        <BarChart data={filteredDaily} margin={{top:5,right:5,left:0,bottom:5}} barCategoryGap="8%">
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(26,45,74,0.15)" vertical={false}/>
          <XAxis dataKey="date" tick={axTick} axisLine={false} tickLine={false} tickFormatter={xFmt} interval={tickInterval||'preserveStartEnd'}/>
          <YAxis tick={axTick} axisLine={false} tickLine={false} tickFormatter={yFmt} width={50}/>
          <ReferenceLine y={0} stroke="rgba(26,45,74,0.4)"/><Tooltip content={tip} cursor={{fill:'rgba(14,165,233,0.06)'}}/>
          <Bar dataKey="pnl" radius={[3,3,0,0]}>{filteredDaily.map(function(e,i){return<Cell key={i} fill={e.pnl>=0?'#00d4aa':'#ff4466'}/>;})}</Bar>
        </BarChart>
      </ResponsiveContainer>}
      {chartTab==='drawdown'&&<ResponsiveContainer width="100%" height={250}>
        <AreaChart data={filteredDaily} margin={{top:5,right:10,left:0,bottom:5}}>
          <defs><linearGradient id="ddG" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#ff4466" stopOpacity={0.25}/><stop offset="95%" stopColor="#ff4466" stopOpacity={0.02}/></linearGradient></defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(26,45,74,0.15)" vertical={false}/>
          <XAxis dataKey="date" tick={axTick} axisLine={false} tickLine={false} tickFormatter={xFmt} interval={tickInterval||'preserveStartEnd'}/>
          <YAxis tick={axTick} axisLine={false} tickLine={false} tickFormatter={yFmt} width={50}/>
          <ReferenceLine y={0} stroke="rgba(26,45,74,0.4)" strokeDasharray="3"/><Tooltip content={tip}/>
          <Area type="monotone" dataKey="dd" stroke="#ff4466" strokeWidth={1.5} fill="url(#ddG)" dot={false}/>
        </AreaChart>
      </ResponsiveContainer>}
    </div>;
  };

  // Strategy cards
  var renderStrategies=function(){
    // Filter: remove deprecated strategies + 'quantiply' group (individual algos show separately)
    var HIDE_KEYS={'auto_dn':true,'autodn':true,'quantiply':true,'straddle':true,'hedged_selling':true,'delta_neutral':true,'true_dn':true,'dn':true,
      // Retired Jun 2026 — removed from engine, hide historical entries from journal
      'prem_asym':true,'prem_asym_1':true,'prem_asym_2':true,'prem_asym_3':true,'premasym':true,'pa':true,'pa1':true,'pa2':true,'pa3':true,
      'scalp_radar':true,'scalpradar':true,'scalp':true,
      'amb_hedged':true,'amb_hedged_1':true,'amb_hedged_2':true,'amb_hedged_3':true,'ambhedged':true,'ambh1':true,'ambh2':true,'ambh3':true};
    var strats=Object.entries(strategies).filter(function(p){return !HIDE_KEYS[p[0]];}).sort(function(a,b){return Math.abs(b[1].cumulative_pnl||0)-Math.abs(a[1].cumulative_pnl||0);});
    return<div>
      <div style={{display:'grid',gridTemplateColumns:isCompact?'repeat(auto-fill,minmax(220px,1fr))':'repeat(auto-fill,minmax(280px,1fr))',gap:10,marginBottom:16}}>
        {strats.map(function(pair){var sid=pair[0],st=pair[1];
          var wr=(st.total_wins+st.total_losses)>0?(st.total_wins/(st.total_wins+st.total_losses)*100):0;
          // Today's P&L
          var todayKey=Object.keys(daily).sort().pop()||'';
          var todayPnl=todayKey&&daily[todayKey][sid]?daily[todayKey][sid].total||0:0;
          var isSelected=selectedAlgo===sid;
          return<div key={sid} onClick={function(){setSelectedAlgo(isSelected?null:sid);setTab('Dashboard');}} style={Object.assign({},card,{borderLeft:'3px solid '+(st.color||'#556677'),cursor:'pointer',outline:isSelected?'2px solid '+(st.color||'#0ea5e9'):'none',outlineOffset:'-2px'})}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
              <div><div style={{fontSize:13,fontWeight:800,color:'#e2e8f0'}}>{st.name}</div><div style={{fontSize:9,color:'#556677'}}>{st.type}</div></div>
              <div style={{textAlign:'right'}}><div style={{fontSize:16,fontWeight:900,color:pc(st.cumulative_pnl||0)}}>{fmtRs(st.cumulative_pnl)}</div>
                <div style={{fontSize:10,color:pc(todayPnl)}}>Today: {fmtRs(todayPnl)}</div></div>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:6,fontSize:10}}>
              <div><span style={{color:'#556677'}}>Trades</span><div style={{color:'#e2e8f0',fontWeight:700}}>{st.total_trades||0}</div></div>
              <div><span style={{color:'#556677'}}>Win Rate</span><div style={{color:wr>=50?'#00d4aa':'#ff4466',fontWeight:700}}>{fmtPct(wr)}</div></div>
              <div><span style={{color:'#556677'}}>W / L</span><div style={{color:'#8899aa',fontWeight:700}}>{st.total_wins||0} / {st.total_losses||0}</div></div>
            </div>
          </div>;
        })}
      </div>
      {/* Daily breakdown table */}
      <div style={card}>
        <div style={{fontSize:11,fontWeight:800,color:'#556677',marginBottom:8}}>DAILY BREAKDOWN</div>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:10}}>
          <thead><tr style={{background:'rgba(10,15,30,0.8)'}}>
            <th style={{padding:'6px 8px',textAlign:'left',color:'#556677',fontWeight:700,fontSize:9}}>Date</th>
            {strats.map(function(p){return<th key={p[0]} style={{padding:'6px 8px',textAlign:'right',color:p[1].color||'#556677',fontWeight:700,fontSize:9}}>{p[1].name}</th>;})}
            <th style={{padding:'6px 8px',textAlign:'right',color:'#e2e8f0',fontWeight:700,fontSize:9}}>Total</th>
          </tr></thead>
          <tbody>{Object.keys(daily).sort().reverse().filter(function(date){
            var sum=daily[date]._summary||{};
            return (sum.total||0)!==0||(sum.trades||0)>0;
          }).map(function(date){
            var day=daily[date];var sum=day._summary||{};
            return<tr key={date} style={{borderBottom:'1px solid rgba(26,45,74,0.15)'}}>
              <td style={{padding:'5px 8px',color:'#8899aa'}}>{fmtDateLabel(date,0)}</td>
              {strats.map(function(p){var v=day[p[0]]?day[p[0]].total||0:0;
                return<td key={p[0]} style={{padding:'5px 8px',textAlign:'right',color:v?pc(v):'#3d4f63',fontWeight:v?700:400}}>{v?fmtRs(v):'-'}</td>;
              })}
              <td style={{padding:'5px 8px',textAlign:'right',fontWeight:800,color:pc(sum.total||0)}}>{fmtRs(sum.total)}</td>
            </tr>;
          })}</tbody>
        </table>
      </div>
    </div>;
  };

  // Calendar
  var renderCalendar=function(){
    var isoMap={};
    dailyData.forEach(function(d){isoMap[d.date]=d;});
    var today=new Date();var todayISO=today.getFullYear()+'-'+String(today.getMonth()+1).padStart(2,'0')+'-'+String(today.getDate()).padStart(2,'0');

    if(calView==='year'){
      var allMonths=[];
      for(var mo=0;mo<12;mo++){
        var daysInM=new Date(calYear,mo+1,0).getDate();var firstDow=new Date(calYear,mo,1).getDay();
        var weeks=[];var week=[];
        for(var pad=0;pad<firstDow;pad++)week.push(null);
        for(var d=1;d<=daysInM;d++){
          var iso=calYear+'-'+String(mo+1).padStart(2,'0')+'-'+String(d).padStart(2,'0');
          week.push({day:d,iso:iso,data:isoMap[iso],isFuture:iso>todayISO});
          if(week.length===7){weeks.push(week);week=[];}
        }if(week.length)weeks.push(week);
        allMonths.push({month:mo,weeks:weeks});
      }
      return<div>
        <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:16}}>
          <button onClick={function(){setCalYear(calYear-1);}} style={{background:'none',border:'1px solid rgba(26,45,74,0.4)',color:'#8899aa',borderRadius:4,padding:'4px 10px',cursor:'pointer',fontSize:12}}>{'\u25C0'}</button>
          <div style={{fontSize:16,fontWeight:900,color:'#e2e8f0'}}>{calYear}</div>
          <button onClick={function(){setCalYear(calYear+1);}} style={{background:'none',border:'1px solid rgba(26,45,74,0.4)',color:'#8899aa',borderRadius:4,padding:'4px 10px',cursor:'pointer',fontSize:12}}>{'\u25B6'}</button>
          <div style={{display:'flex',gap:4,marginLeft:16}}>
            {[['month','Month'],['year','Year']].map(function(v){return<button key={v[0]} onClick={function(){setCalView(v[0]);}} style={{padding:'3px 10px',borderRadius:4,fontSize:10,fontWeight:700,cursor:'pointer',fontFamily:'inherit',background:calView===v[0]?'rgba(14,165,233,0.15)':'transparent',color:calView===v[0]?'#0ea5e9':'#556677',border:'1px solid '+(calView===v[0]?'rgba(14,165,233,0.3)':'rgba(26,45,74,0.3)')}}>{v[1]}</button>;})}
          </div>
        </div>
        <div style={{display:'flex',flexWrap:'wrap',gap:8,justifyContent:'center'}}>
          {allMonths.map(function(m){
            return<div key={m.month} style={{cursor:'pointer',minWidth:isCompact?75:90}} onClick={function(){setCalMonth(m.month);setCalView('month');}}>
              <div style={{fontSize:10,fontWeight:700,color:'#8899aa',marginBottom:3,textAlign:'center'}}>{MONTHS[m.month]}</div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(7,14px)',gap:2,justifyContent:'center'}}>
                {m.weeks.map(function(wk,wi){return wk.map(function(cell,ci){
                  if(!cell)return<div key={wi+'e'+ci} style={{width:14,height:14}}/>;
                  var bg='#1a2030';
                  if(cell.isFuture)bg='#141822';
                  else if(cell.data){var cp=cell.data.pnl,ct=cell.data.trades||0;if(cp===0||ct===0){bg='#1a2030';}else{var int_=Math.min(1,Math.abs(cp)/50000);bg=cp>0?'hsl(162,'+(40+int_*40)+'%,'+(22+int_*22)+'%)':'hsl(350,'+(40+int_*40)+'%,'+(22+int_*22)+'%)';}}
                  return<div key={cell.iso} style={{width:14,height:14,borderRadius:2,background:bg,border:cell.iso===todayISO?'1px solid #0ea5e9':'none'}} title={cell.iso+(cell.data?' '+fmtRs(cell.data.pnl):'')}/>;
                });})}
              </div>
            </div>;
          })}
        </div>
      </div>;
    }

    var firstDay=new Date(calYear,calMonth,1).getDay();var daysInMonth=new Date(calYear,calMonth+1,0).getDate();
    var cells=[];var monthTotal=0;
    for(var e=0;e<firstDay;e++)cells.push({type:'empty'});
    for(var d=1;d<=daysInMonth;d++){
      var iso=calYear+'-'+String(calMonth+1).padStart(2,'0')+'-'+String(d).padStart(2,'0');
      var dd=isoMap[iso];var isFuture=iso>todayISO;
      if(dd)monthTotal+=dd.pnl;
      cells.push({day:d,iso:iso,data:dd,isFuture:isFuture,isToday:iso===todayISO,type:'day'});
    }
    return<div>
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:16}}>
        <button onClick={function(){if(calMonth===0){setCalMonth(11);setCalYear(calYear-1);}else setCalMonth(calMonth-1);}} style={{background:'none',border:'1px solid rgba(26,45,74,0.4)',color:'#8899aa',borderRadius:4,padding:'4px 10px',cursor:'pointer',fontSize:12}}>{'\u25C0'}</button>
        <div style={{fontSize:15,fontWeight:800,color:'#e2e8f0',minWidth:140,textAlign:'center'}}>{MONTHS[calMonth]} {calYear}</div>
        <button onClick={function(){if(calMonth===11){setCalMonth(0);setCalYear(calYear+1);}else setCalMonth(calMonth+1);}} style={{background:'none',border:'1px solid rgba(26,45,74,0.4)',color:'#8899aa',borderRadius:4,padding:'4px 10px',cursor:'pointer',fontSize:12}}>{'\u25B6'}</button>
        <div style={{display:'flex',gap:4,marginLeft:16}}>
          {[['month','Month'],['year','Year']].map(function(v){return<button key={v[0]} onClick={function(){setCalView(v[0]);}} style={{padding:'3px 10px',borderRadius:4,fontSize:10,fontWeight:700,cursor:'pointer',fontFamily:'inherit',background:calView===v[0]?'rgba(14,165,233,0.15)':'transparent',color:calView===v[0]?'#0ea5e9':'#556677',border:'1px solid '+(calView===v[0]?'rgba(14,165,233,0.3)':'rgba(26,45,74,0.3)')}}>{v[1]}</button>;})}
        </div>
        <div style={{marginLeft:'auto',fontSize:14,fontWeight:900,color:pc(monthTotal)}}>{fmtRs(monthTotal)}</div>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:2,marginBottom:4}}>
        {DAYS_F.map(function(d){return<div key={d} style={{textAlign:'center',fontSize:9,fontWeight:700,color:'#556677',padding:'4px 0'}}>{d}</div>;})}
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:2}}>
        {cells.map(function(c,i){
          if(c.type==='empty')return<div key={'e'+i} style={{height:72}}/>;
          var bg='rgba(13,21,38,0.4)';var tc='#3d4f63';
          if(c.isFuture)bg='rgba(20,28,45,0.3)';
          else if(c.data){if(c.data.pnl===0||(c.data.trades||0)===0){bg='rgba(13,21,38,0.4)';tc='#3d4f63';}else{bg=c.data.pnl>0?'rgba(0,212,170,0.08)':'rgba(255,68,102,0.08)';tc=pc(c.data.pnl);}}
          return<div key={c.iso} style={{height:72,borderRadius:4,padding:'4px 6px',background:bg,border:c.isToday?'1px solid #0ea5e9':'1px solid rgba(26,45,74,0.12)'}}>
            <div style={{fontSize:11,fontWeight:c.isToday?800:600,color:c.isFuture?'#2a3548':c.isToday?'#0ea5e9':'#8899aa'}}>{c.day}</div>
            {c.data&&<div><div style={{fontSize:12,fontWeight:900,color:tc,marginTop:2}}>{fmtRs(c.data.pnl)}</div><div style={{fontSize:8,color:'#556677'}}>{c.data.trades}t</div></div>}
          </div>;
        })}
      </div>
    </div>;
  };

  // Dashboard
  var renderDashboard=function(){
    var todayPnl=dailyData.length?dailyData[dailyData.length-1].pnl:0;
    return<div>
      <div style={{display:'grid',gridTemplateColumns:isCompact?'repeat(auto-fit,minmax(90px,1fr))':'repeat(auto-fit,minmax(110px,1fr))',gap:8,marginBottom:16}}>
        {metric('Total P&L',fmtRs(totalP),pc(totalP),daysRecorded+' days')}
        {metric('Win Rate',fmtPct(winR),winR>=50?'#00d4aa':'#ff4466',totalW+'W / '+totalL+'L')}
        {metric('Trades',String(totalT),'#e2e8f0',selectedAlgo?'1 strategy':visibleStrats.length+' strategies')}
        {metric('Today',fmtRs(todayPnl),pc(todayPnl))}
        {metric('Sharpe',String(riskMetrics.sharpe),riskMetrics.sharpe>=1?'#00d4aa':riskMetrics.sharpe>=0?'#fbbf24':'#ff4466','annualized')}
        {metric('Sortino',String(riskMetrics.sortino),riskMetrics.sortino>=1.5?'#00d4aa':riskMetrics.sortino>=0?'#fbbf24':'#ff4466','annualized')}
        {metric('Max DD',fmtRs(-riskMetrics.maxDD),'#ff4466','peak to trough')}
        {metric('Profit Factor',riskMetrics.profitFactor===Infinity?'\u221E':String(riskMetrics.profitFactor),riskMetrics.profitFactor>=1.5?'#00d4aa':riskMetrics.profitFactor>=1?'#fbbf24':'#ff4466','gross W/L')}
      </div>
      {renderCharts()}
      <div style={{display:'grid',gridTemplateColumns:isCompact?'repeat(auto-fill,minmax(220px,1fr))':'repeat(auto-fill,minmax(280px,1fr))',gap:10}}>
        {Object.entries(strategies).filter(function(p){return !HIDE_STRAT[p[0]];}).sort(function(a,b){return Math.abs(b[1].cumulative_pnl||0)-Math.abs(a[1].cumulative_pnl||0);}).map(function(pair){
          var sid=pair[0],st=pair[1];
          var todayKey=Object.keys(daily).sort().pop()||'';
          var todayPnl=todayKey&&daily[todayKey][sid]?daily[todayKey][sid].total||0:0;
          var isSelected=selectedAlgo===sid;
          return<div key={sid} onClick={function(){setSelectedAlgo(isSelected?null:sid);}} style={Object.assign({},card,{borderLeft:'3px solid '+(st.color||'#556677'),cursor:'pointer',outline:isSelected?'2px solid '+(st.color||'#0ea5e9'):'none',outlineOffset:'-2px',transition:'outline 0.15s'})}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
              <div><div style={{fontSize:13,fontWeight:800,color:'#e2e8f0'}}>{st.name}</div><div style={{fontSize:9,color:'#556677'}}>{st.type}</div></div>
              <div style={{textAlign:'right'}}><div style={{fontSize:16,fontWeight:900,color:pc(st.cumulative_pnl||0)}}>{fmtRs(st.cumulative_pnl)}</div>
                <div style={{fontSize:10,color:pc(todayPnl)}}>Today: {fmtRs(todayPnl)}</div></div>
            </div>
          </div>;
        })}
      </div>
    </div>;
  };

  return<div style={{display:'flex',flexDirection:'column',height:'100%',background:'#080e1d',color:'#e2e8f0',fontFamily:"'Inter',sans-serif",overflow:'hidden'}}>
    <div style={{display:'flex',alignItems:'center',gap:12,padding:'10px 18px',background:'linear-gradient(135deg,#0d1526,#0a1020)',borderBottom:'1px solid rgba(26,45,74,0.5)',flexShrink:0,flexWrap:'wrap'}}>
      <div>
        {selectedAlgo?
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            <button onClick={function(){setSelectedAlgo(null);}} style={{padding:'4px 10px',borderRadius:4,fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:'inherit',background:'rgba(100,100,120,0.15)',color:'#8899aa',border:'1px solid rgba(100,100,120,0.3)'}}>{'\u2190'} All</button>
            <div>
              <div style={{fontSize:16,fontWeight:900,display:'flex',alignItems:'center',gap:8}}><span style={{width:10,height:10,borderRadius:2,background:selectedAlgoInfo?selectedAlgoInfo.color:'#8899aa',display:'inline-block'}}/>{selectedAlgoInfo?selectedAlgoInfo.name:selectedAlgo}</div>
              <div style={{fontSize:10,color:'#556677'}}>{selectedAlgoInfo?selectedAlgoInfo.type:''} · {daysRecorded} active days</div>
            </div>
          </div>
          :<div><div style={{fontSize:16,fontWeight:900}}>Trading Journal</div><div style={{fontSize:10,color:'#556677'}}>Portfolio analytics · {daysRecorded} days</div></div>
        }
      </div>
      <div style={{marginLeft:'auto',display:'flex',alignItems:'center',gap:10}}>
        <button onClick={function(){setPerLot(!perLot);}} title="Divide P&L by each day's traded lots so days and strategies are comparable per unit of risk" style={{padding:'6px 14px',borderRadius:4,fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:'inherit',background:perLot?'rgba(0,212,170,0.15)':'rgba(100,100,120,0.1)',color:perLot?'#00d4aa':'#8899aa',border:'1px solid '+(perLot?'rgba(0,212,170,0.45)':'rgba(100,100,120,0.3)')}}>{perLot?'\u2713 Per Lot':'Per Lot'}</button>
        <button onClick={doRefresh} disabled={refreshing} style={{padding:'6px 14px',borderRadius:4,fontSize:11,fontWeight:700,cursor:refreshing?'wait':'pointer',fontFamily:'inherit',background:'rgba(14,165,233,0.1)',color:'#0ea5e9',border:'1px solid rgba(14,165,233,0.3)'}}>{refreshing?'Updating...':'\u21BB Update'}</button>
        <button onClick={doBackfill} disabled={backfilling} style={{padding:'6px 14px',borderRadius:4,fontSize:11,fontWeight:700,cursor:backfilling?'wait':'pointer',fontFamily:'inherit',background:'rgba(167,139,250,0.1)',color:'#a78bfa',border:'1px solid rgba(167,139,250,0.3)'}}>{backfilling?'Importing...':'\u2B07 Backfill'}</button>
        <div style={{textAlign:'right'}}><div style={{fontSize:isCompact?18:22,fontWeight:900,color:pc(totalP)}}>{fmtRs(totalP)}</div><div style={{fontSize:10,color:'#556677'}}>{totalT} trades</div></div>
      </div>
    </div>
    <div style={{display:'flex',borderBottom:'1px solid rgba(26,45,74,0.35)',flexShrink:0,background:'rgba(13,21,38,0.5)'}}>
      {TABS.map(function(t){return<button key={t} onClick={function(){setTab(t);}} style={{padding:'9px 20px',fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:'inherit',border:'none',background:'transparent',color:tab===t?'#0ea5e9':'#556677',borderBottom:tab===t?'2px solid #0ea5e9':'2px solid transparent'}}>{t}</button>;})}
    </div>
    <div style={{flex:1,overflowY:'auto',padding:18}}>
      {tab==='Dashboard'&&renderDashboard()}
      {tab==='Strategies'&&renderStrategies()}
      {tab==='Calendar'&&renderCalendar()}
    </div>
  </div>;
}
